import {
  parseProject,
  parseResource,
  filterResources,
  resourceQuerySchema,
  type ResourceQuery,
  tenantSettingsSchema,
  type Project,
  type Repository,
  type CreationOptions,
  type ProjectPage,
  type ProjectPageQuery,
  projectPageQuerySchema,
  type ProjectCatalogPage,
  type Revision,
  type PublishedVersion,
  type ProofRecord,
  type Resource,
  type TenantSettings,
} from "@proof/core";
import type { ProofClient } from "./index";

/** A repository bound to an already authenticated tenant; the server owns identity and authorship. */
export class ApiRepository implements Repository {
  async catalog(tenant: string, input: ProjectPageQuery = {}, signal?: AbortSignal): Promise<ProjectCatalogPage> {
    this.scope(tenant);
    const query = projectPageQuerySchema.parse(input);
    const params = new URLSearchParams(Object.entries(query).map(([key, value]) => [key, String(value)]));
    params.set("projection", "summary");
    const result = await this.client.request<ProjectCatalogPage>(`/projects?${params}`, {signal});
    if (!Array.isArray(result.items) || !Number.isSafeInteger(result.total) || result.total < 0 || !Number.isSafeInteger(result.offset) || result.offset < 0 || result.limit !== query.limit || result.items.length > query.limit || result.items.some(item => item.tenantId !== tenant || typeof item.id !== "string" || !Number.isSafeInteger(item.fieldCount) || !Number.isSafeInteger(item.outputCount))) throw Error("Invalid project catalog page");
    return result;
  }
  constructor(
    readonly tenantId: string,
    private readonly client: Pick<ProofClient, "request">,
  ) {
    if (!tenantId || tenantId.length > 200)
      throw new Error("Invalid repository tenant");
  }
  private scope(tenant: string) {
    if (tenant !== this.tenantId) throw new Error("Repository tenant mismatch");
  }
  private project(value: unknown, expectedId?: string) {
    const project = parseProject(value);
    this.scope(project.tenantId);
    if (expectedId !== undefined && project.id !== expectedId)
      throw new Error("Repository project mismatch");
    return project;
  }
  private path(id: string) {
    return `/projects/${encodeURIComponent(id)}`;
  }
  async list(tenant: string) {
    this.scope(tenant);
    return (await this.client.request<unknown[]>("/projects")).map((value) =>
      this.project(value),
    );
  }
  async page(
    tenant: string,
    input: ProjectPageQuery = {},
    signal?: AbortSignal,
  ): Promise<ProjectPage> {
    this.scope(tenant);
    const query = projectPageQuerySchema.parse(input);
    const params = new URLSearchParams(
      Object.entries(query).map(([key, value]) => [key, String(value)]),
    );
    const result = await this.client.request<ProjectPage>(
      `/projects?${params}`,
      { signal },
    );
    if (
      !Array.isArray(result.items) ||
      !Number.isSafeInteger(result.total) ||
      result.total < 0 ||
      !Number.isSafeInteger(result.offset) ||
      result.offset < 0 ||
      result.limit !== query.limit ||
      result.items.length > query.limit
    )
      throw new Error("Invalid project page");
    return {
      ...result,
      items: result.items.map((value) => this.project(value)),
    };
  }
  async get(tenant: string, id: string) {
    this.scope(tenant);
    try {
      return this.project(await this.client.request(this.path(id)), id);
    } catch (error) {
      if ((error as { status?: number }).status === 404) return undefined;
      throw error;
    }
  }
  async save(tenant: string, project: Project, expectedRevision: number) {
    this.scope(tenant);
    this.project(project);
    return this.project(
      await this.client.request(this.path(project.id), {
        method: "PUT",
        body: JSON.stringify({ project, expectedRevision }),
      }),
      project.id,
    );
  }
  async create(
    tenant: string,
    definition: unknown,
    options: CreationOptions = {},
  ) {
    this.scope(tenant);
    const project = this.project(definition);
    if (options.validate) await options.validate(project);
    return this.project(
      await this.client.request("/projects", {
        method: "POST",
        body: JSON.stringify({
          project,
          resources: options.resources,
          useDefaults: options.useDefaults !== false,
        }),
      }),
    );
  }
  async remove(tenant: string, id: string) {
    this.scope(tenant);
    await this.client.request(this.path(id), { method: "DELETE" });
  }
  private revision(value: Revision, id: string) {
    if (value.projectId !== id) throw new Error("Repository project mismatch");
    this.project(value.definition, id);
    return value;
  }
  async history(tenant: string, id: string) {
    this.scope(tenant);
    return (
      await this.client.request<Revision[]>(`${this.path(id)}/history`)
    ).map((row) => this.revision(row, id));
  }
  async restore(
    tenant: string,
    id: string,
    revisionId: string,
    expectedRevision: number | null,
  ) {
    this.scope(tenant);
    return this.project(
      await this.client.request(`${this.path(id)}/restore`, {
        method: "POST",
        body: JSON.stringify({ revisionId, expectedRevision }),
      }),
      id,
    );
  }
  async lockRevision(
    tenant: string,
    id: string,
    revisionId: string,
    locked: boolean,
  ) {
    this.scope(tenant);
    const { revision } = await this.client.request<{
      revision: Revision | null;
    }>(`${this.path(id)}/history/${encodeURIComponent(revisionId)}/lock`, {
      method: "PUT",
      body: JSON.stringify({ locked }),
    });
    return revision ? this.revision(revision, id) : undefined;
  }
  async tenantSettings(tenant: string) {
    this.scope(tenant);
    const result = tenantSettingsSchema.parse(
      await this.client.request("/tenant-settings"),
    );
    this.scope(result.tenantId);
    return result;
  }
  async saveTenantSettings(
    tenant: string,
    settings: TenantSettings,
    expectedRevision: number,
  ) {
    this.scope(tenant);
    this.scope(settings.tenantId);
    const result = tenantSettingsSchema.parse(
      await this.client.request("/tenant-settings", {
        method: "PUT",
        body: JSON.stringify({ settings, expectedRevision }),
      }),
    );
    this.scope(result.tenantId);
    return result;
  }
  private version(row: PublishedVersion, id: string) {
    if (row.projectId !== id || this.project(row.definition).id !== id)
      throw new Error("Repository project mismatch");
    return row;
  }
  async publish(tenant: string, id: string) {
    this.scope(tenant);
    return this.version(
      await this.client.request<PublishedVersion>(`${this.path(id)}/publish`, {
        method: "POST",
      }),
      id,
    );
  }
  async versions(tenant: string, id: string) {
    this.scope(tenant);
    return (
      await this.client.request<PublishedVersion[]>(`${this.path(id)}/versions`)
    ).map((row) => this.version(row, id));
  }
  private proof(row: ProofRecord, id: string) {
    this.scope(row.tenantId || "");
    if (row.projectId !== id) throw new Error("Repository project mismatch");
    return row;
  }
  async saveProof(tenant: string, record: ProofRecord) {
    this.scope(tenant);
    if (record.tenantId) this.scope(record.tenantId);
    const {
      versionId,
      partId,
      name,
      values,
      comments,
      accepted,
      initials,
      contextHash,
      sourceParameters,
      profileSelections,
    } = record;
    return this.proof(
      await this.client.request<ProofRecord>(
        `${this.path(record.projectId)}/proofs`,
        {
          method: "POST",
          body: JSON.stringify({
            versionId,
            partId,
            name,
            values,
            comments,
            accepted,
            initials,
            contextHash,
            sourceParameters,
            profileSelections,
          }),
        },
      ),
      record.projectId,
    );
  }
  async proofs(tenant: string, id: string, ownerSubjectId?: string) {
    this.scope(tenant);
    const rows = (
      await this.client.request<ProofRecord[]>(`${this.path(id)}/proofs`)
    ).map((row) => this.proof(row, id));
    return ownerSubjectId
      ? rows.filter((row) => row.ownerSubjectId === ownerSubjectId)
      : rows;
  }
  private resource(value: Resource) {
    const row = parseResource(value);
    this.scope(row.tenantId);
    if (value.archived !== undefined && typeof value.archived !== "boolean")
      throw new Error("Invalid resource state");
    return value.archived === undefined
      ? row
      : { ...row, archived: value.archived };
  }
  async resources(tenant: string, query?: ResourceQuery) {
    this.scope(tenant);
    const params = new URLSearchParams(
      Object.entries(resourceQuerySchema.parse(query || {})).filter(
        (entry): entry is [string, string] => typeof entry[1] === "string",
      ),
    );
    return filterResources(
      (
        await this.client.request<Resource[]>(
          `/resources${params.size ? `?${params}` : ""}`,
        )
      ).map((value) => this.resource(value)),
      query,
    );
  }
  async saveResource(
    tenant: string,
    resource: Resource,
    expectedVersion = resource.version,
  ) {
    this.scope(tenant);
    this.resource(resource);
    return this.resource(
      await this.client.request<Resource>("/resources", {
        method: "POST",
        body: JSON.stringify({ ...resource, expectedVersion }),
      }),
    );
  }
  async archiveResource(tenant: string, id: string, archived: boolean) {
    this.scope(tenant);
    await this.client.request(`/resources/${encodeURIComponent(id)}/archive`, {
      method: "PUT",
      body: JSON.stringify({ archived }),
    });
  }
}
